import { Check, Minus } from "@phosphor-icons/react"
import {
  Checkbox as AriaCheckbox,
  type CheckboxProps as AriaCheckboxProps,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"

export type CheckboxProps = Omit<AriaCheckboxProps, "className"> & {
  className?: string
  /** If the checkbox is indeterminate. */
  isIndeterminate?: boolean
  /** If the checkbox is checked. */
  isSelected?: boolean
  size?: "small" | "medium" | "large"
}

/**
 * A control that allows users to toggle between checked, unchecked, and indeterminate states.
 *
 * ```tsx
 * import { Checkbox } from "@tokenterminal/core/ui/input/checkbox/Checkbox"
 *
 * <Checkbox
 *   label="Label"
 *   isSelected={isSelected}
 *   onChange={setIsSelected}
 * />
 * ```
 */

export const Checkbox = ({
  children,
  size = "medium",
  className,
  ...props
}: CheckboxProps) => {
  return (
    <AriaCheckbox
      className={twMerge("tt-checkbox-wrapper", className)}
      data-size={size}
      {...props}
    >
      {(state) => (
        <>
          <div className="tt-checkbox-icon-wrapper">
            {state.isIndeterminate ? (
              <Minus weight="bold" className="tt-checkbox-svg" />
            ) : (
              <Check weight="bold" className="tt-checkbox-svg" />
            )}
          </div>
          {typeof children === "function" ? children(state) : children}
        </>
      )}
    </AriaCheckbox>
  )
}
